import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { ModalitiesProvider } from "@/contexts/ModalitiesContext";
import { UploadProgressProvider, useUploadProgress } from "@/contexts/UploadProgressContext";
import { registerUploadActiveCheck } from "@/lib/api";
import { useEffect, useRef } from "react";
import ProtectedRoute from "@/components/ProtectedRoute";
import PermissionRoute from "@/components/PermissionRoute";
import DefaultRedirect from "@/components/DefaultRedirect";
import AppLayout from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import WeeklyDashboard from "@/pages/WeeklyDashboard";
import BillingDashboard from "@/pages/BillingDashboard";
import Patients from "@/pages/Patients";
import PatientDetail from "@/pages/PatientDetail";
import Organizations from "@/pages/Organizations";
import CompanyDetail from "@/pages/CompanyDetail";
import Studies from "@/pages/Studies";
import PendingStudyDeletions from "@/pages/PendingStudyDeletions";
import Upload from "@/pages/Upload";
import ImageViewer from "@/pages/ImageViewer";
import SharingRoute from "@/components/SharingRoute";
import AuditTrail from "@/pages/AuditTrail";
import UserManagement from "@/pages/UserManagement";
import DashboardCardsSettings from "@/pages/DashboardCardsSettings";
import SearchPage from "@/pages/SearchPage";
import Flowchart from "@/pages/Flowchart";
import Reports from "@/pages/Reports";
import ReportDetail from "@/pages/ReportDetail";
import SharedDownload from "@/pages/SharedDownload";
import PrivacyPolicy from "@/pages/PrivacyPolicy";

function UploadProgressRegistrar() {
  const { isUploadActive, setShareTargetUsers } = useUploadProgress();
  const location = useLocation();
  const wasOnUploadRef = useRef(location.pathname.startsWith("/upload"));

  useEffect(() => {
    registerUploadActiveCheck(isUploadActive);
  }, [isUploadActive]);

  useEffect(() => {
    const onUpload = location.pathname.startsWith("/upload");
    if (wasOnUploadRef.current && !onUpload) {
      setShareTargetUsers([]);
    }
    wasOnUploadRef.current = onUpload;
  }, [location.pathname, setShareTargetUsers]);

  return null;
}

function App() {
  return (
    <UploadProgressProvider>
      <AuthProvider>
        <ModalitiesProvider>
          <BrowserRouter>
            <UploadProgressRegistrar />
            <Toaster
              theme="light"
              position="top-right"
              richColors
              closeButton
              toastOptions={{
                classNames: {
                  toast: "text-[15px] sm:text-base py-3.5 px-4 min-h-[52px] gap-3",
                  title: "text-[15px] sm:text-base font-medium leading-snug",
                  description: "text-sm sm:text-[15px] leading-snug",
                  closeButton: "scale-110",
                },
              }}
            />
            <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/sharing" element={<SharingRoute />} />
          <Route path="/shared/:token" element={<SharedDownload />} />

          {/* Protected app routes */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            {/* Admin/owner dashboard — requires manage_reference_data */}
            <Route
              path="/"
              element={
                <PermissionRoute path="/">
                  <Dashboard />
                </PermissionRoute>
              }
            />

            <Route
              path="/weekly-dashboard"
              element={
                <PermissionRoute path="/weekly-dashboard">
                  <WeeklyDashboard />
                </PermissionRoute>
              }
            />

            <Route
              path="/billing"
              element={
                <PermissionRoute path="/billing">
                  <BillingDashboard />
                </PermissionRoute>
              }
            />

            <Route
              path="/patients"
              element={
                <PermissionRoute path="/patients">
                  <Patients />
                </PermissionRoute>
              }
            />
            <Route
              path="/patients/:uhid"
              element={
                <PermissionRoute path="/patients">
                  <PatientDetail />
                </PermissionRoute>
              }
            />
            <Route
              path="/organizations"
              element={
                <PermissionRoute path="/organizations">
                  <Organizations />
                </PermissionRoute>
              }
            />
            <Route
              path="/organizations/:id"
              element={
                <PermissionRoute path="/organizations">
                  <CompanyDetail />
                </PermissionRoute>
              }
            />
            <Route
              path="/hospitals"
              element={
                <PermissionRoute path="/organizations">
                  <Navigate to="/organizations" replace />
                </PermissionRoute>
              }
            />
            <Route
              path="/studies"
              element={
                <PermissionRoute path="/studies">
                  <Studies />
                </PermissionRoute>
              }
            />
            <Route
              path="/studies/pending-deletions"
              element={
                <PermissionRoute path="/studies/pending-deletions">
                  <PendingStudyDeletions />
                </PermissionRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <PermissionRoute path="/reports">
                  <Reports />
                </PermissionRoute>
              }
            />
            <Route
              path="/reports/:reportId"
              element={
                <PermissionRoute path="/reports">
                  <ReportDetail />
                </PermissionRoute>
              }
            />
            <Route
              path="/viewer"
              element={
                <PermissionRoute path="/viewer">
                  <ImageViewer />
                </PermissionRoute>
              }
            />
            <Route
              path="/viewer/:studyId"
              element={
                <PermissionRoute path="/viewer">
                  <ImageViewer />
                </PermissionRoute>
              }
            />
            <Route
              path="/search"
              element={
                <PermissionRoute path="/search">
                  <SearchPage />
                </PermissionRoute>
              }
            />

            <Route
              path="/upload"
              element={
                <PermissionRoute path="/upload">
                  <Upload />
                </PermissionRoute>
              }
            />


            {/* Admin-only routes */}
            <Route
              path="/users"
              element={
                <PermissionRoute path="/users">
                  <UserManagement />
                </PermissionRoute>
              }
            />
            <Route
              path="/dashboard-cards"
              element={
                <PermissionRoute path="/dashboard-cards">
                  <DashboardCardsSettings />
                </PermissionRoute>
              }
            />
            <Route
              path="/audit"
              element={
                <PermissionRoute path="/audit">
                  <AuditTrail />
                </PermissionRoute>
              }
            />

            <Route
              path="/flowchart"
              element={
                <PermissionRoute path="/flowchart">
                  <Flowchart />
                </PermissionRoute>
              }
            />

            {/* Unknown paths → role default landing page */}
            <Route path="*" element={<DefaultRedirect />} />
          </Route>
            </Routes>
          </BrowserRouter>
        </ModalitiesProvider>
      </AuthProvider>
    </UploadProgressProvider>
  );
}

export default App;
